/*
eslint-disable @typescript-eslint/no-explicit-any,
import/no-unresolved,
@typescript-eslint/no-unused-vars,
max-len,
no-case-declarations
*/
import React from 'react';
import { InlineTool } from '@editorjs/editorjs/types/tools/inline-tool';
import { API } from '@editorjs/editorjs';
import {
  InstructionCSSClasses,
  BaseSaveInstructionData,
  InstructionAttribute, InstructionEvent,
} from '../types';
import { detectIOS } from '../../../../utility/helpers';
import UndoAndRedoListener from '../../../../services/editorJS/event-listeners/UndoAndRedoListener';

type ConstructorParams = {
  api: API;
};

type InstructionClasses = {
  wrapper: InstructionCSSClasses;
  content: InstructionCSSClasses;
  action: InstructionCSSClasses;
};

abstract class InlineInstruction implements InlineTool {
  public static isInline = true;

  protected static title = '';

  protected readonly api: API;

  protected currentBlockIndex: number = 0;

  abstract get instructionClasses(): InstructionClasses;

  abstract get icon(): string;

  abstract openModal(instructionId: string): void;

  abstract getTooltip(tooltip: string): string;

  protected actionCSS: {
    base: string;
    active: string;
  };

  protected static TAG = 'span';

  protected tag = 'span';

  protected action: HTMLButtonElement;

  protected range: Range | null;

  protected isActive: boolean;

  constructor({ api }: ConstructorParams) {
    this.api = api;
    this.range = null;
    this.actionCSS = {
      base: this.api.styles.inlineToolButton,
      active: this.api.styles.inlineToolButtonActive,
    };
    this.isActive = false;
    this.action = this.initAction();
  }

  initAction() {
    const action = document.createElement<'button'>('button');
    action.type = 'button';
    action.classList.add(this.actionCSS.base);
    action.innerHTML = this.icon;
    return action;
  }

  /**
   * Create button element for Toolbar
   *
   * @return {HTMLElement}
   */
  render() {
    return this.action;
  }

  /**
   * Wrap/Unwrap selected fragment
   *
   * @param {Range} range - selected fragment
   */
  surround(range: Range) {
    if (!range) {
      this.range = null;
      return;
    }
    this.currentBlockIndex = this.api.blocks.getCurrentBlockIndex();
    this.range = range;
    const termWrapper = this.getInstructionWrapper();
    let instructionId = '';
    if (termWrapper) {
      instructionId = termWrapper.getAttribute(InstructionAttribute.instructionId) || '';
      this.isActive = true;
      this.api.selection.expandToTag(termWrapper);
      const sel = window.getSelection();
      if (sel) {
        this.range = sel.getRangeAt(0);
      }
      // this.unwrap(termWrapper);
    } else {
      this.isActive = false;
      // this.wrap();
    }

    // Open Modal
    this.api.events.emit(InstructionEvent.SET_PREVENT_ON_CHANGE_EDITOR, true);
    this.openModal(instructionId);
  }

  /**
   * Sanitizer rule
   * @return {{span: {class: string}}}
   */
  static get sanitize() {
    return {
      [this.TAG]: true,
    };
  }

  /**
   * Wrap selection with term-tag
   */
  wrap(data: BaseSaveInstructionData) {
    const wrapper = document.createElement(this.tag);
    const action = document.createElement(this.tag);
    const content = document.createElement(this.tag);
    wrapper.setAttribute(InstructionAttribute.instructionId, data.instructionId);

    if (data.tooltip !== undefined) {
      action.setAttribute(
        InstructionAttribute.tooltip,
        this.getTooltip(data.tooltip),
      );
    }
    if (data.data !== undefined) {
      wrapper.setAttribute(
        InstructionAttribute.data,
        data.data,
      );
    }
    wrapper.classList.add(
      InstructionCSSClasses.INLINE_INSTRUCTION_WRAPPER,
      this.instructionClasses.wrapper,
    );
    action.classList.add(
      InstructionCSSClasses.INLINE_INSTRUCTION_ACTION,
      this.instructionClasses.action,
    );
    content.classList.add(
      InstructionCSSClasses.INLINE_INSTRUCTION_CONTENT,
      this.instructionClasses.content,
    );
    wrapper.contentEditable = 'true';
    action.contentEditable = 'false';
    content.contentEditable = 'true';

    if (detectIOS()) {
      action.onmouseenter = this.actionHandler;
    }

    action.onclick = this.actionHandler;
    if (this.range) {
      content.appendChild(this.range.extractContents());
      wrapper.appendChild(action);
      wrapper.appendChild(content);
      this.range.insertNode(wrapper);
      this.isActive = true;
    }

    this.setSelection();
    this.api.events.emit(InstructionEvent.SUBMIT_DATA, {});
    UndoAndRedoListener.setSelectionToCurrentBlock(this.api, this.currentBlockIndex).then(() => UndoAndRedoListener.addState());
  }

  setSelection(): void {
    const selection = window.getSelection();
    if (selection && this.range) {
      selection.removeAllRanges();
      selection.addRange(this.range);
      selection.collapseToEnd();
    }
  }

  actionHandler = (event: MouseEvent | TouchEvent) => {
    const mappedTarget = event.target as HTMLSpanElement;
    const { parentElement } = mappedTarget;
    if (parentElement) {
      const instructionId = parentElement.getAttribute(InstructionAttribute.instructionId);
      if (instructionId) {
        this.openModal(instructionId);
      }
    }
  };

  /**
   * Unwrap term-tag
   *
   * @param {HTMLElement} termWrapper - term wrapper tag
   */
  unwrap(termWrapper: HTMLElement) {
    /**
     * Expand selection to all term-tag
     */
    this.api.selection.expandToTag(termWrapper);

    const sel = window.getSelection();

    if (sel) {
      const range = sel.getRangeAt(0);

      const unwrappedContent = range.extractContents();

      /**
       * Remove empty term-tag
       */
      termWrapper.parentNode?.removeChild(termWrapper);

      /**
       * Insert extracted content
       */
      if (unwrappedContent) {
        range.insertNode(unwrappedContent);
      }

      /**
       * Restore selection
       */
      sel.removeAllRanges();
      sel.addRange(range);
      this.isActive = false;
    }
  }

  /**
   * Check and change Term's state for current selection
   */
  checkState() {
    const termTag = this.api.selection.findParentTag(
      this.tag,
      this.instructionClasses.wrapper,
    );
    this.action.classList.toggle(this.actionCSS.active, !!termTag);

    return !!termTag;
  }

  getInstructionWrapper() {
    return this.api.selection.findParentTag(
      this.tag,
      this.instructionClasses.wrapper,
    );
  }
}

export default InlineInstruction;
